import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowCounterClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowCounterClockwise';
import { CaretDownIcon } from '@phosphor-icons/react/dist/csr/CaretDown';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';
import { TrashIcon } from '@phosphor-icons/react/dist/csr/Trash';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Textarea } from '../../design-system/primitives/Textarea/Textarea';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import {
  isStructuralTemplateCode,
  smsTemplateManagementApi,
  type SmsTemplate,
  type SmsTemplateVariableState,
  type SmsTemplateVersion,
} from '../../lib/api/sms';
import styles from './TemplateEditor.module.css';

/**
 * Create/edit an SMS template, plus (edit only) its version history and
 * revert — M-18, Task 6, Step 3. Follows `ArticleFormDialog.tsx` for the
 * overall shape: one component, `template` absent is create, present is
 * edit.
 *
 * ## A save that fails validation keeps what the user typed (this task's
 * own brief)
 *
 * `state` is never reset on a caught `ApiError` — only on a successful
 * save (`onSaved`, which closes the dialog) or on open/re-open
 * (the `useEffect` below, keyed on `template`). A validation failure
 * updates `fieldErrors`/`generalFailure` alone.
 *
 * ## Field errors are shown AGAINST THE FIELD, per `CodeUploadPanel.tsx`'s
 * precedent this task names
 *
 * `TemplateContract::check()` (API repository, verified) attaches every
 * placeholder-contract violation to the `body` key regardless of which
 * declaration it actually names (`StoreSmsTemplateRequest`/
 * `UpdateSmsTemplateRequest`'s own `withValidator()`, verified) — so the
 * body field is where those messages render. `code`'s structural-rename
 * refusal and `is_active`'s structural-deactivation refusal are each their
 * own keys and render against `code`/the active checkbox respectively. A
 * 422 keyed to anything else falls back to the general failure banner
 * (`RENDERED_FIELDS`, the identical `ArticleFormDialog.tsx` guard) rather
 * than vanishing.
 *
 * ## `locale`/`channel` are editable on CREATE only
 *
 * `UpdateSmsTemplateRequest` marks both `prohibited` — sending either at
 * all, any value, is a `422` (verified) — so this form never sends them on
 * an edit, and does not offer editable controls for them either; edit mode
 * shows both as plain read-only text.
 *
 * ## A structural template's `code`/`is_active` controls are disabled, not
 * merely validated against
 *
 * The API refuses a structural rename or deactivation unconditionally
 * (`UpdateSmsTemplateRequest::withValidator()`, verified) — offering an
 * editable control that always fails on submit is the exact "a button
 * that always fails is worse than not offering it" defect this task's
 * brief names for delete. Both controls are disabled with an explanatory
 * hint instead, the same reasoning `TemplateCatalogue.tsx` applies to its
 * own delete button.
 *
 * ## `knownIsActive`, and why `is_active` is not sent on every edit
 * (fix round 1, C-1)
 *
 * `SmsTemplateResource` never reports `is_active` on the wire (see
 * `sms.ts`'s own docblock), so this form cannot ask the server "is this
 * template currently active?" — the ONLY source for that fact is
 * `TemplateCatalogue.tsx`'s own derived `TemplateCatalogueRow.is_active`,
 * threaded in as `knownIsActive`. The checkbox is seeded from it
 * (`stateFor`), and `isActiveTouched` tracks whether the person actually
 * clicked it during THIS open. `handleSubmit` sends `is_active` on a
 * non-structural edit ONLY when `isActiveTouched` is true — never merely
 * because the checkbox happens to read a particular value, since an
 * untouched checkbox reflects `knownIsActive` (or a default of `true`
 * when that is itself unknown), not a decision the user made. Before this
 * fix, `is_active: true` was sent on every edit unconditionally, silently
 * reactivating a template opened for an unrelated typo fix — the exact
 * door `SmsTemplateCatalogueController`'s own "a retired template must
 * never reach a patient" docblock closes elsewhere.
 *
 * ## Variables are a small dynamic list, not a raw JSON textarea
 *
 * `variables` is `Record<string, 'required' | 'optional' | 'line'>` on the
 * wire. A raw JSON textarea would let a typo become a silent parse
 * failure at submit; a name + level row per declaration, added and removed
 * explicitly, cannot produce malformed JSON at all. A declared variable
 * the body does not use is legal (`TemplateContract`'s own docblock,
 * `line` variables) — this form enforces nothing stricter than the server.
 */

export interface TemplateEditorProps {
  open: boolean;
  template?: SmsTemplate;
  /**
   * `TemplateCatalogue.tsx`'s own derived active state for `template` —
   * `null` when unknown (the picker call failed or the caller lacks
   * `sms.view`), `undefined` only because there is no template yet
   * (create). See this module's own docblock, "`knownIsActive`, and why
   * `is_active` is not sent on every edit" (fix round 1, C-1).
   */
  knownIsActive?: boolean | null;
  onClose: () => void;
  /**
   * `appliedIsActive` is this form's OWN record of what the just-sent
   * request actually SET — never a display-only guess. It is `null`
   * exactly when the request did not carry `is_active` at all (fix round
   * 2, n-1): an edit of a non-structural template whose checkbox nobody
   * touched sends no `is_active` key (see `handleSubmit`'s own comment),
   * so there is nothing true to report here. `TemplateCatalogue.tsx`'s
   * own docblock explains why the wire cannot answer this question
   * itself; this field must not paper over "the request set nothing"
   * with the checkbox's merely-displayed value, or the catalogue row
   * ends up claiming a state the request never asked for.
   */
  onSaved: (template: SmsTemplate, appliedIsActive: boolean | null) => void;
}

interface VariableRow {
  key: string;
  name: string;
  state: SmsTemplateVariableState;
}

type TemplateChannel = 'sms' | 'email';
type TemplateLocale = 'en' | 'ar';

interface FormState {
  code: string;
  name: string;
  description: string;
  channel: TemplateChannel;
  locale: TemplateLocale;
  body: string;
  variableRows: readonly VariableRow[];
  category: string;
  isActive: boolean;
}

let variableRowSeq = 0;
function nextVariableKey(): string {
  variableRowSeq += 1;
  return `var-${variableRowSeq}`;
}

function variablesToRows(variables: Readonly<Record<string, SmsTemplateVariableState>>): VariableRow[] {
  return Object.entries(variables).map(([name, state]) => ({ key: nextVariableKey(), name, state }));
}

function rowsToVariables(rows: readonly VariableRow[]): Record<string, SmsTemplateVariableState> {
  const out: Record<string, SmsTemplateVariableState> = {};
  for (const row of rows) {
    const name = row.name.trim();
    if (name === '') continue;
    out[name] = row.state;
  }
  return out;
}

function stateFor(template: SmsTemplate | undefined, knownIsActive: boolean | null): FormState {
  if (template === undefined) {
    return {
      code: '',
      name: '',
      description: '',
      channel: 'sms',
      locale: 'ar',
      body: '',
      variableRows: [],
      category: '',
      isActive: true,
    };
  }
  return {
    code: template.code,
    name: template.name,
    description: template.description ?? '',
    // The wire types `channel`/`locale` as plain `string` (`SmsTemplate`'s
    // own docblock — the send picker's shape, reused here) since the
    // renderer treats them opaquely; this form only ever displays them on
    // edit (never sends either back, both `prohibited`), so a value
    // outside the two-member union it renders falls back to a safe
    // default rather than widening `FormState.channel`/`.locale` back to
    // `string` for the sake of a case that never happens in practice.
    channel: template.channel === 'email' ? 'email' : 'sms',
    locale: template.locale === 'en' ? 'en' : 'ar',
    body: template.body,
    variableRows: variablesToRows(template.variables),
    category: template.category ?? '',
    // Seeded from the CALLER's derived state (`knownIsActive`), never a
    // bare assumption — see this module's own "`knownIsActive`" docblock
    // (fix round 1, C-1). A structural template is always active in
    // practice (the API refuses to ever clear it) and its checkbox is
    // disabled regardless of this value, so `true` there costs nothing;
    // an ordinary template with `knownIsActive === null` (truly unknown)
    // still defaults to `true` for the same reason the create form does
    // — but critically, `isActiveTouched` (below) being `false` means
    // this default is never what actually reaches the PATCH unless the
    // person deliberately toggles the checkbox.
    isActive: isStructuralTemplateCode(template.code) ? true : (knownIsActive ?? true),
  };
}

const VARIABLE_STATES: readonly SmsTemplateVariableState[] = ['required', 'optional', 'line'];
const CHANNELS: readonly TemplateChannel[] = ['sms', 'email'];
const LOCALES: readonly TemplateLocale[] = ['en', 'ar'];

/** i18next types `t()` against the real bundle (`i18next.d.ts`, task 19) —
 *  a template-string key (`` `smsTemplates.channel.${value}` ``) does not
 *  satisfy that, so each closed vocabulary gets its own lookup record, the
 *  identical `CATEGORY_LABEL_KEY`/`LOCALE_LABEL_KEY` shape
 *  `ArticleFormDialog.tsx`'s sibling module (`articleLabels.ts`) already
 *  uses for the same reason. */
const CHANNEL_LABEL_KEY = {
  sms: 'smsTemplates.channel.sms',
  email: 'smsTemplates.channel.email',
} as const satisfies Record<TemplateChannel, string>;

const LOCALE_LABEL_KEY = {
  en: 'smsTemplates.locale.en',
  ar: 'smsTemplates.locale.ar',
} as const satisfies Record<TemplateLocale, string>;

const VARIABLE_STATE_LABEL_KEY = {
  required: 'smsTemplates.variableState.required',
  optional: 'smsTemplates.variableState.optional',
  line: 'smsTemplates.variableState.line',
} as const satisfies Record<SmsTemplateVariableState, string>;

const FORBIDDEN_CODE = 'forbidden';
const RENDERED_FIELDS: ReadonlySet<string> = new Set(['code', 'name', 'description', 'body', 'category', 'is_active']);

export function TemplateEditor({
  open,
  template,
  knownIsActive = null,
  onClose,
  onSaved,
}: TemplateEditorProps) {
  const { t } = useTranslation();
  const isEdit = template !== undefined;
  const structural = template !== undefined && isStructuralTemplateCode(template.code);
  const uid = useId();

  const [state, setState] = useState<FormState>(() => stateFor(template, knownIsActive));
  /** Fix round 1, C-1. `true` only once the person actually clicks the
   *  active checkbox during THIS open — see this module's own
   *  `knownIsActive` docblock for why an untouched checkbox must never be
   *  read as an intentional "make this active". */
  const [isActiveTouched, setIsActiveTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [versions, setVersions] = useState<readonly SmsTemplateVersion[]>([]);
  const [versionsNextCursor, setVersionsNextCursor] = useState<string | null>(null);
  const [versionsStatus, setVersionsStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [revertingVersion, setRevertingVersion] = useState<number | null>(null);
  const [revertFailure, setRevertFailure] = useState<string | null>(null);

  const activeFieldId = `${uid}-active`;
  const activeHintId = `${uid}-active-hint`;
  const activeErrorId = `${uid}-active-error`;

  const inFlight = useRef(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setState(stateFor(template, knownIsActive));
    setIsActiveTouched(false);
    setFieldErrors({});
    setGeneralFailure(null);
    setVersions([]);
    setVersionsNextCursor(null);
    setVersionsStatus('idle');
    setRevertFailure(null);
    // `knownIsActive` deliberately NOT a dependency (fix round 2, n-3:
    // this comment previously claimed it prevents a re-seed if the
    // catalogue refreshes while the dialog is open — false, since
    // `template` IS `editingTemplate`, the same row object
    // `knownIsActive` is derived from, so a catalogue refresh changes
    // `template` too and re-seeds regardless of this exclusion). The
    // real reason: `knownIsActive` is re-derived by the caller from
    // `template` on every render, so listing it here would be a
    // redundant, always-equal-when-`template`-is-equal dependency, not a
    // guard against anything. Nothing today refreshes the catalogue
    // while this dialog is open, so the re-seed case this comment used
    // to warn about does not currently arise either way.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template]);

  const templateUuid = template?.uuid;

  async function loadVersions(cursor: string | null): Promise<void> {
    if (templateUuid === undefined) return;
    setVersionsStatus('loading');
    try {
      const page = await smsTemplateManagementApi.versions(templateUuid, {
        ...(cursor === null ? {} : { cursor }),
      });
      setVersions((current) => (cursor === null ? page.data : [...current, ...page.data]));
      setVersionsNextCursor(page.meta.next_cursor);
      setVersionsStatus('ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setVersionsStatus('error');
    }
  }

  useEffect(() => {
    if (!open || templateUuid === undefined) return;
    void loadVersions(null);
    // Reload keyed on the dialog opening for this specific template only —
    // `loadVersions` itself is not a dependency (it closes over state that
    // changes every render) the same way `TemplateCatalogue.tsx`'s `load`
    // avoids re-running from its own state updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateUuid]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('smsTemplates.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('smsTemplates.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('smsTemplates.saveFailed');
    }
  }

  /**
   * The revert path's own failure copy (fix round 1, M-1). Unlike
   * `handleSubmit`, this screen has no per-field slot to render a revert's
   * `422` against — `SmsTemplateVersionController::revert()` re-runs
   * `TemplateContract::check()` against the TARGET VERSION's own
   * `body`/`variables` (verified) and answers `422` with the SAME
   * `{body: [...]}` shape a `PATCH` does when the contract has tightened
   * since that version was written. `RENDERED_FIELDS`-style field errors
   * make no sense here (there is no live form field to blame; the version
   * being restored is the problem), so the server's own first message is
   * shown as this path's OWN failure text instead of the generic
   * `saveFailed` sentence a revert never actually is.
   */
  function describeRevertFailure(error: ApiError): string {
    for (const messages of Object.values(error.details)) {
      const first = messages[0];
      if (first !== undefined) return first;
    }
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('smsTemplates.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('smsTemplates.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('smsTemplates.revertFailed');
    }
  }

  function addVariableRow(): void {
    setState((current) => ({
      ...current,
      variableRows: [...current.variableRows, { key: nextVariableKey(), name: '', state: 'required' }],
    }));
  }

  function removeVariableRow(key: string): void {
    setState((current) => ({
      ...current,
      variableRows: current.variableRows.filter((row) => row.key !== key),
    }));
  }

  function updateVariableRow(key: string, changes: Partial<Pick<VariableRow, 'name' | 'state'>>): void {
    setState((current) => ({
      ...current,
      variableRows: current.variableRows.map((row) => (row.key === key ? { ...row, ...changes } : row)),
    }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralFailure(null);

    const variables = rowsToVariables(state.variableRows);

    try {
      const saved =
        isEdit && template !== undefined
          ? await smsTemplateManagementApi.updateTemplate(template.uuid, {
              ...(structural ? {} : { code: state.code.trim() }),
              name: state.name.trim(),
              description: state.description.trim() === '' ? null : state.description.trim(),
              body: state.body,
              variables,
              category: state.category.trim() === '' ? null : state.category.trim(),
              // Fix round 1, C-1. Sent ONLY when the person actually
              // touched the checkbox this open — never merely because it
              // holds a particular value, since an untouched checkbox
              // reflects `knownIsActive` (or an assumed default when that
              // is itself unknown), not a decision anyone made. Omitting
              // the key is `PATCH`'s own "leave alone"
              // (`UpdateSmsTemplateRequest::rules()`, `sometimes`).
              ...(structural || !isActiveTouched ? {} : { is_active: state.isActive }),
            })
          : await smsTemplateManagementApi.createTemplate({
              code: state.code.trim(),
              name: state.name.trim(),
              description: state.description.trim() === '' ? null : state.description.trim(),
              channel: state.channel,
              locale: state.locale,
              body: state.body,
              variables,
              category: state.category.trim() === '' ? null : state.category.trim(),
              is_active: state.isActive,
            });
      // Fix round 2, n-1. Report what the request actually SET, not what
      // the checkbox merely displays: a structural row is always active
      // (the API refuses to ever clear it, regardless of what was sent);
      // a create always sends `is_active` (see the payload above), so
      // `state.isActive` is genuinely what the server was told; a
      // non-structural EDIT sends the key only when `isActiveTouched`
      // (the guard on the payload just above) — when it did not, nobody
      // asked the server to change anything, and `null` says so, leaving
      // `TemplateCatalogue.tsx`'s `handleSaved` to keep the row's
      // existing (possibly still-unknown) state rather than fabricate one.
      onSaved(saved, structural ? true : isEdit ? (isActiveTouched ? state.isActive : null) : state.isActive);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      const details = error.details;
      const mapped: Record<string, string> = {};
      for (const [field, messages] of Object.entries(details)) {
        const first = messages[0];
        if (first !== undefined) mapped[field] = first;
      }
      const hasRenderedField = Object.keys(mapped).some((field) => RENDERED_FIELDS.has(field));
      if (hasRenderedField) {
        setFieldErrors(mapped);
      } else {
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  async function handleRevert(version: SmsTemplateVersion): Promise<void> {
    if (template === undefined || revertingVersion !== null) return;
    setRevertingVersion(version.version);
    setRevertFailure(null);

    try {
      const reverted = await smsTemplateManagementApi.revert(template.uuid, version.version);
      setState((current) => ({
        ...current,
        body: reverted.body,
        variableRows: variablesToRows(reverted.variables),
      }));
      await loadVersions(null);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setRevertFailure(describeRevertFailure(error));
    } finally {
      setRevertingVersion(null);
    }
  }

  const channelOptions: readonly SelectOption[] = CHANNELS.map((value) => ({
    value,
    label: t(CHANNEL_LABEL_KEY[value]),
  }));
  const localeOptions: readonly SelectOption[] = LOCALES.map((value) => ({
    value,
    label: t(LOCALE_LABEL_KEY[value]),
  }));
  const variableStateOptions: readonly SelectOption[] = VARIABLE_STATES.map((value) => ({
    value,
    label: t(VARIABLE_STATE_LABEL_KEY[value]),
  }));

  const dialogTitle =
    template === undefined
      ? t('smsTemplates.createTitle')
      : t('smsTemplates.editTitle', { name: template.name });

  return (
    <Dialog open={open} kind="form" size="lg" onClose={onClose} initialFocusRef={nameRef} title={dialogTitle}>
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true" data-testid="templateEditorFailure">
          {generalFailure}
        </div>

        <Input
          ref={nameRef}
          label={t('smsTemplates.fieldName')}
          className={styles['half']}
          width="fill"
          required
          value={state.name}
          error={fieldErrors['name']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, name: value }));
          }}
        />

        <Input
          label={t('smsTemplates.fieldCode')}
          className={styles['half']}
          width="fill"
          dir="ltr"
          required
          disabled={structural}
          hint={structural ? t('smsTemplates.structuralCodeLocked') : undefined}
          value={state.code}
          error={fieldErrors['code']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, code: value }));
          }}
        />

        <Input
          label={t('smsTemplates.fieldCategory')}
          className={styles['half']}
          width="fill"
          value={state.category}
          error={fieldErrors['category']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, category: value }));
          }}
        />

        {isEdit ? (
          <p className={styles['readOnlyRow']}>
            {t('smsTemplates.fieldChannel')}: {t(CHANNEL_LABEL_KEY[state.channel])}
            {' · '}
            {t('smsTemplates.fieldLocale')}: {t(LOCALE_LABEL_KEY[state.locale])}
          </p>
        ) : (
          // Both a two/three-option choice — genuinely short in content even
          // though the shared `Select` chrome marks them `fill` — so they take
          // one row between them inside the wider `lg` dialog rather than two
          // stacked rows each a couple of words wide. No declared
          // `flex-direction`: `.row`'s default already reverses under
          // `dir="rtl"`.
          <div className={styles['row']}>
            <Select
              label={t('smsTemplates.fieldChannel')}
              width="fill"
              required
              options={channelOptions}
              value={state.channel}
              onChange={(event) => {
                const value = event.target.value as TemplateChannel;
                setState((current) => ({ ...current, channel: value }));
              }}
            />
            <Select
              label={t('smsTemplates.fieldLocale')}
              width="fill"
              required
              options={localeOptions}
              value={state.locale}
              onChange={(event) => {
                const value = event.target.value as TemplateLocale;
                setState((current) => ({ ...current, locale: value }));
              }}
            />
          </div>
        )}

        <Input
          label={t('smsTemplates.fieldDescription')}
          width="fill"
          value={state.description}
          error={fieldErrors['description']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, description: value }));
          }}
        />

        {/*
          Fix round 1, m-1. The label text and the control now live in
          SEPARATE elements — a wrapping `<label>` around both the control
          AND the error span (the previous shape) folds the error text
          into the control's accessible name, so after a 422 the textarea
          read as "Message body Body uses {{nickname}}, which the
          template does not declare…" to a screen reader on every later
          visit, not merely the moment the error appeared. `aria-invalid`/
          `aria-describedby` are the correct, narrower association
          instead — the same contract `Field.tsx` already gives `Input`/
          `Select`.

          Fix round 3. The hand-built label / textarea / error trio above
          IS `Textarea` — a `Field` with a multi-line control — so it is
          that now. Every property the two fix rounds argued for survives
          the swap because `Field.tsx` already holds them: the label is a
          sibling of the control rather than its parent (round 1's m-1),
          the error reaches the control through `aria-describedby` and
          `aria-invalid` rather than through the accessible name, and the
          label-to-control gap is `--space-2` from `.field` rather than
          `.form`'s `--space-4` (round 2's n-2). What it also gains is the
          border, ground and focus edge every OTHER field on this form
          has — the `--color-border-hairline` on `--color-surface` the
          bespoke sheet used made this box nearly invisible against the
          panel it sits on.

          `rows={6}`: an SMS body is the longest free text this
          application asks anyone to type.
        */}
        <Textarea
          label={t('smsTemplates.fieldBody')}
          dir="auto"
          rows={6}
          value={state.body}
          error={fieldErrors['body']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, body: value }));
          }}
        />

        <div className={styles['variablesBlock']}>
          <p className={styles['variablesTitle']}>{t('smsTemplates.fieldVariables')}</p>

          {state.variableRows.map((row, index) => {
            // Fix round 1, M-2. Falls back to the row's POSITION when the
            // name is still blank (a freshly added row) — there is
            // nothing else to distinguish it by yet, and a shared empty
            // label would be exactly the "series of unnamed comboboxes"
            // defect this fix exists for.
            const rowLabel = row.name.trim() === '' ? String(index + 1) : row.name.trim();
            return (
              <div key={row.key} className={styles['variableRow']}>
                <input
                  type="text"
                  dir="ltr"
                  className={styles['variableName']}
                  placeholder={t('smsTemplates.variableNamePlaceholder')}
                  aria-label={t('smsTemplates.variableNameAria', { position: index + 1 })}
                  value={row.name}
                  onChange={(event) => {
                    updateVariableRow(row.key, { name: event.target.value });
                  }}
                />
                <select
                  className={styles['variableState']}
                  value={row.state}
                  aria-label={t('smsTemplates.variableStateAria', { name: rowLabel })}
                  onChange={(event) => {
                    updateVariableRow(row.key, { state: event.target.value as SmsTemplateVariableState });
                  }}
                >
                  {variableStateOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={t('smsTemplates.removeVariableAria', { name: rowLabel })}
                  onClick={() => removeVariableRow(row.key)}
                  icon={<TrashIcon size={16} />}
                >
                  {t('smsTemplates.removeVariable')}
                </Button>
              </div>
            );
          })}

          <Button type="button" variant="secondary" onClick={addVariableRow} icon={<PlusIcon size={16} />}>
            {t('smsTemplates.addVariable')}
          </Button>
        </div>

        {/*
          Fix round 1, m-2. Same fix shape as the body field above: the
          hint and the error are each a SEPARATE element from the
          `<label>`, joined to the checkbox via `aria-describedby` (both
          ids, space-joined, exactly when each exists) rather than folded
          into the label's own text.
        */}
        <div className={styles['activeRow']}>
          <input
            type="checkbox"
            id={activeFieldId}
            checked={structural ? true : state.isActive}
            disabled={structural}
            aria-invalid={fieldErrors['is_active'] === undefined ? undefined : true}
            aria-describedby={
              [structural ? activeHintId : null, fieldErrors['is_active'] === undefined ? null : activeErrorId]
                .filter((id): id is string => id !== null)
                .join(' ') || undefined
            }
            onChange={(event) => {
              const { checked } = event.target;
              setIsActiveTouched(true);
              setState((current) => ({ ...current, isActive: checked }));
            }}
          />
          <label htmlFor={activeFieldId}>{t('smsTemplates.fieldActive')}</label>
        </div>
        {structural ? (
          <p id={activeHintId} className={styles['hint']}>
            {t('smsTemplates.structuralActiveLocked')}
          </p>
        ) : null}
        {fieldErrors['is_active'] === undefined ? null : (
          <span id={activeErrorId} className={styles['fieldError']} role="alert">
            {fieldErrors['is_active']}
          </span>
        )}

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={<FloppyDiskIcon size={16} />}>
            {t('smsTemplates.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('smsTemplates.cancel')}
          </Button>
        </div>

        {isEdit ? (
          <div className={styles['versionsBlock']}>
            <p className={styles['variablesTitle']}>{t('smsTemplates.versionHistory')}</p>

            {versionsStatus === 'loading' && versions.length === 0 ? (
              <p className={styles['hint']}>{t('smsTemplates.versionsLoading')}</p>
            ) : null}
            {versionsStatus === 'error' ? (
              <p className={styles['fieldError']} role="alert">
                {t('smsTemplates.versionsLoadFailed')}
              </p>
            ) : null}
            {versionsStatus === 'ready' && versions.length === 0 ? (
              <p className={styles['hint']}>{t('smsTemplates.versionsEmpty')}</p>
            ) : null}

            <ul className={styles['versionList']}>
              {versions.map((version) => (
                <li key={version.uuid} className={styles['versionRow']}>
                  <span className={styles['versionMeta']}>
                    {t('smsTemplates.versionLabel', { version: version.version })}
                    {version.change_note === null ? null : ` — ${version.change_note}`}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    loading={revertingVersion === version.version}
                    disabled={revertingVersion !== null && revertingVersion !== version.version}
                    onClick={() => {
                      void handleRevert(version);
                    }}
                    icon={<ArrowCounterClockwiseIcon size={16} />}
                  >
                    {t('smsTemplates.revert')}
                  </Button>
                </li>
              ))}
            </ul>

            {revertFailure === null ? null : (
              <p className={styles['fieldError']} role="alert">
                {revertFailure}
              </p>
            )}

            {versionsNextCursor === null ? null : (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  void loadVersions(versionsNextCursor);
                }}
                icon={<CaretDownIcon size={16} />}
              >
                {t('smsTemplates.loadMoreVersions')}
              </Button>
            )}
          </div>
        ) : null}
      </form>
    </Dialog>
  );
}
